import type { DateRange } from "react-day-picker"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { format, subDays } from "date-fns"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconChevronLeftOutline18 as ChevronLeft,
	IconChevronRightOutline18 as ChevronRight,
} from "nucleo-ui-outline-18"

import { Button } from "./button"
import { DateRangePicker } from "./date-range-picker"
import { IconButton } from "./icon-button"
import { DropdownMenu, DropdownMenuItem } from "./internal/dropdown"

export type DateRangeType =
	| "7days"
	| "4weeks"
	| "3months"
	| "12months"
	| "all"
	| "custom"

interface DateRangeOption {
	label: string
	value: DateRangeType
	days?: number
}

const DATE_RANGE_OPTIONS: DateRangeOption[] = [
	{ label: "Last 7 days", value: "7days", days: 7 },
	{ label: "Last 4 weeks", value: "4weeks", days: 28 },
	{ label: "Last 3 months", value: "3months", days: 90 },
	{ label: "Last 12 months", value: "12months", days: 365 },
	{ label: "All time", value: "all" },
	{ label: "Custom", value: "custom" },
]

interface DateRangeSelectorProps {
	onChange: (startDate: Date | undefined, endDate: Date | undefined) => void
	onKeyboardShortcut?: (handler: {
		navigateBackward: () => void
		navigateForward: () => void
		canNavigateBackward: boolean
		canNavigateForward: boolean
	}) => void
	onRangeTypeChange?: (rangeType: DateRangeType) => void
	initialRangeType?: DateRangeType
	initialCustomRange?: { from: Date; to: Date }
}

function DateRangeSelectorInner({
	onChange,
	onKeyboardShortcut,
	onRangeTypeChange,
	initialRangeType = "4weeks",
	initialCustomRange,
}: DateRangeSelectorProps) {
	const [rangeType, setRangeType] = useState<DateRangeType>(initialRangeType)
	const [customRange, setCustomRange] = useState<DateRange | undefined>(
		initialCustomRange,
	)
	const [offset, setOffset] = useState(0)

	const currentOption = useMemo(
		() => DATE_RANGE_OPTIONS.find((opt) => opt.value === rangeType),
		[rangeType],
	)

	const handleRangeTypeChange = useCallback(
		(newRangeType: DateRangeType) => {
			setRangeType(newRangeType)
			setOffset(0)
			onRangeTypeChange?.(newRangeType)

			if (newRangeType === "custom") {
				// Don't trigger onChange yet, wait for user to select dates
				return
			}

			if (newRangeType === "all") {
				onChange(undefined, undefined)
				return
			}

			const days =
				DATE_RANGE_OPTIONS.find((opt) => opt.value === newRangeType)?.days || 7
			const now = new Date()
			const start = subDays(now, days)
			onChange(start, now)
		},
		[onChange, onRangeTypeChange],
	)

	const handleCustomRangeChange = useCallback(
		(range: DateRange | undefined) => {
			setCustomRange(range)
			if (range?.from && range?.to) {
				onChange(range.from, range.to)
			}
		},
		[onChange],
	)

	const handleNavigateBackward = useCallback(() => {
		if (rangeType === "all" || rangeType === "custom") {
			return
		}

		const days = currentOption?.days || 28
		const newOffset = offset + days
		setOffset(newOffset)

		const now = new Date()
		const end = subDays(now, newOffset)
		const start = subDays(end, days)
		onChange(start, end)
	}, [rangeType, currentOption, offset, onChange])

	const handleNavigateForward = useCallback(() => {
		if (rangeType === "all" || rangeType === "custom" || offset === 0) {
			return
		}

		const days = currentOption?.days || 28
		const newOffset = Math.max(0, offset - days)
		setOffset(newOffset)

		const now = new Date()
		const end = subDays(now, newOffset)
		const start = subDays(end, days)
		onChange(start, end)
	}, [rangeType, currentOption, offset, onChange])

	const handleResetToToday = useCallback(() => {
		if (rangeType === "all" || rangeType === "custom") {
			return
		}

		setOffset(0)

		const days = currentOption?.days || 28
		const now = new Date()
		const start = subDays(now, days)
		onChange(start, now)
	}, [rangeType, currentOption, onChange])

	const isNavigationDisabled = rangeType === "all" || rangeType === "custom"
	const isForwardDisabled = isNavigationDisabled || offset === 0

	// Notify parent of initial range type on mount
	useEffect(() => {
		onRangeTypeChange?.(rangeType)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Expose keyboard shortcut handlers to parent
	useMemo(() => {
		if (onKeyboardShortcut) {
			onKeyboardShortcut({
				navigateBackward: handleNavigateBackward,
				navigateForward: handleNavigateForward,
				canNavigateBackward: !isNavigationDisabled,
				canNavigateForward: !isForwardDisabled,
			})
		}
	}, [
		onKeyboardShortcut,
		handleNavigateBackward,
		handleNavigateForward,
		isNavigationDisabled,
		isForwardDisabled,
	])

	// Keyboard shortcuts for arrow keys
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement
			const isInputOrTextarea =
				target.tagName === "INPUT" ||
				target.tagName === "TEXTAREA" ||
				target.contentEditable === "true"

			if (isInputOrTextarea) {
				return
			}

			if (event.key === "ArrowLeft" && !isNavigationDisabled) {
				event.preventDefault()
				handleNavigateBackward()
			} else if (event.key === "ArrowRight" && !isForwardDisabled) {
				event.preventDefault()
				handleNavigateForward()
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => {
			window.removeEventListener("keydown", handleKeyDown)
		}
	}, [
		handleNavigateBackward,
		handleNavigateForward,
		isNavigationDisabled,
		isForwardDisabled,
	])

	const currentDates = useMemo(() => {
		if (rangeType === "all") {
			return { start: undefined, end: undefined }
		}

		if (rangeType === "custom" && customRange?.from && customRange?.to) {
			return { start: customRange.from, end: customRange.to }
		}

		if (rangeType === "custom") {
			return { start: undefined, end: undefined }
		}

		const days = currentOption?.days || 28
		const now = new Date()
		const end = subDays(now, offset)
		const start = subDays(end, days)

		return { start, end }
	}, [rangeType, customRange, offset, currentOption])

	const displayLabel = useMemo(() => {
		if (rangeType === "all") {
			return "All time"
		}

		if (rangeType === "custom" && customRange?.from && customRange?.to) {
			return `${format(customRange.from, "MMM d, yyyy")} - ${format(customRange.to, "MMM d, yyyy")}`
		}

		if (rangeType === "custom") {
			return "Select date range"
		}

		if (offset === 0) {
			return currentOption?.label || "Last 4 weeks"
		}

		if (currentDates.start && currentDates.end) {
			return `${format(currentDates.start, "MMM d, yyyy")} - ${format(currentDates.end, "MMM d, yyyy")}`
		}

		return currentOption?.label || "Last 4 weeks"
	}, [rangeType, customRange, offset, currentOption, currentDates])

	return (
		<div className="flex items-center gap-2">
			<IconButton
				Icon={ChevronLeft}
				variant="ghost"
				onClick={handleNavigateBackward}
				disabled={isNavigationDisabled}
				className="h-8 w-8"
			/>

			<IconButton
				Icon={ChevronRight}
				variant="ghost"
				onClick={handleNavigateForward}
				disabled={isForwardDisabled}
				className={isForwardDisabled ? "h-8 w-8 opacity-50" : "h-8 w-8"}
			/>

			<DropdownMenu
				trigger={
					<Button
						variant="outline"
						iconRight={ChevronDown}
						className="w-42 pl-3"
					>
						{displayLabel}
					</Button>
				}
			>
				{DATE_RANGE_OPTIONS.map((option) => (
					<DropdownMenuItem
						key={option.value}
						onSelect={() => handleRangeTypeChange(option.value)}
					>
						{option.label}
					</DropdownMenuItem>
				))}
			</DropdownMenu>

			{offset > 0 && rangeType !== "all" && rangeType !== "custom" && (
				<Button variant="outline" onClick={handleResetToToday}>
					Today
				</Button>
			)}

			{rangeType === "custom" && (
				<DateRangePicker
					dateRange={customRange}
					setDateRange={handleCustomRangeChange}
					className="h-8"
				/>
			)}
		</div>
	)
}

export const DateRangeSelector = memo(DateRangeSelectorInner)
